import React from 'react';

import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import {
  focusSource,
  focusWithin,
  memoizedPartial,
} from '@wix/wix-vod-shared/common';
import { Loadable } from '@wix/wix-vod-shared/components';
import { onEscPress } from '../../utils/call-on-keyboard-press';

import { getWixSDK } from '../../utils/wix-sdk';
import { logWidgetSystem } from '../../worker/actions/bi';

import TooltipPopout from '../../components/tooltip/tooltip-popout';

import CLOSE_REASONS from '../constants/close-reasons';

import { getPublicChannel } from '../../redux/actions/channel/get-public';

import { getChannelLayout, getAllSettings } from '../../selectors/app-settings';
import FocusLock from 'react-focus-lock';

import coreStyles from '../../styles/core.scss';
import styles from './full-screen-modals-layout.scss';

import { clearHydratedData } from '../../redux/hydrated-data/hydrated-data';

const mapStateToProps = (state) => ({
  layout: getChannelLayout(state),
  appSettings: getAllSettings(state),
});

const mapDispatchToProps = {
  getPublicChannel,
  clearHydratedData,
  logWidgetSystem,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  class FullScreenModalsLayout extends React.Component {
    static propTypes = {
      getPublicChannel: PropTypes.func,
      children: PropTypes.func,
      layout: PropTypes.number,
      match: PropTypes.object,
      clearHydratedData: PropTypes.func.isRequired,
    };

    state = {
      isOpen: true,
    };

    componentDidMount() {
      document.addEventListener(
        'keyup',
        memoizedPartial(onEscPress, this.close),
      );
      this.focusWithinHandler = focusWithin();
      // This removes loader-spinner element from initial DOM.
      // It can't be inside <div id="root"> because in this case it will be removed before everything loads.
      // UX matters ,)
      const loader = document.getElementById('loader-spinner');
      if (loader) {
        loader.remove();
      }
    }

    componentWillUnmount() {
      this.focusWithinHandler.disengage();
      focusSource.disengage(this.containerRef);
      document.removeEventListener(
        'keyup',
        memoizedPartial(onEscPress, this.close),
      );
    }

    loadInitialData = () => {
      const { channelId } = this.props.match.params;

      return Promise.all([
        // Todo: load channel on route change if needed
        channelId ? this.props.getPublicChannel(channelId) : Promise.resolve(),
      ]).then(() => this.props.clearHydratedData());
    };

    handleDataLoaded = () => {
      this.props.logWidgetSystem('fullscreenModal.load.rendered', {
        previousEventName: 'fullscreenModal.load.requested',
      });
    };

    close = (reason = CLOSE_REASONS.CLOSED_BY_USER) => {
      this.props.logWidgetSystem('fullscreenModal.close');
      this.setState({
        isOpen: false,
      });
      getWixSDK().closeWindow({ reason });
    };

    stopPropagation = (e) => {
      e.stopPropagation();
    };

    renderContent() {
      const { children } = this.props;
      if (!children) {
        return null;
      }

      return children({ close: this.close });
    }

    saveContainerRef = (ref) => {
      if (!ref) {
        return;
      }

      focusSource.engage(ref);
      this.containerRef = ref;
    };

    render() {
      const { isOpen } = this.state;
      const { layout } = this.props;

      const styleNames = classnames(coreStyles.root, styles.overlay, {
        [styles.opened]: isOpen,
      });

      return (
        <Loadable
          loader={this.loadInitialData}
          onDataLoaded={this.handleDataLoaded}
        >
          <FocusLock>
            <div
              ref={this.saveContainerRef}
              className={styleNames}
              data-layout={layout}
              data-hook="fullScreenModalsLayoutWrapper"
              onClick={memoizedPartial(this.close, null)}
            >
              <div
                onClick={this.stopPropagation}
                data-hook="fullScreenModalsLayoutContent"
              >
                {this.renderContent()}
                <TooltipPopout />
              </div>
            </div>
          </FocusLock>
        </Loadable>
      );
    }
  },
);
